import React from "react";
import _ from "lodash";
import { NetworkAPI } from "apis";
import ModalWrapper from "components/common/ModalWrapper";
import ModalFooter from "components/common/ModalFooter";
import EditableJsonEditor from "components/common/EditableJsonEditor";
import UPRSettingsTable from "./NetworkUPRSettingsTable";

import {
  cleanupRule,
  groupLabels,
  validateRule,
  validateRuleCustom,
} from "helpers/CustomUprFirstLookUtils";
import { buttonModalConfirmClass } from "helpers/StyleClass.js";
import { MESSAGE } from "constants/Message";

class EditCustomUPRDataModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      configData: _.cloneDeep(props.currentData),
      editValidated: true,

      labels: [],
      configsByLabel: {},
      msg: [],
      isValidating: false,
      validatedPass: false,
    };

    this.handleConfigDataChanged = this.handleConfigDataChanged.bind(this);
    this.handleSyncErrMsg = this.handleSyncErrMsg.bind(this);
    this.handleValidateData = this.handleValidateData.bind(this);
    this.handleDataChanged = this.handleDataChanged.bind(this);
  }

  componentDidMount() {
    const { configData } = this.state;
    const { labels, configsByLabel } = groupLabels(configData, configData);
    this.setState({ labels, configsByLabel });
  }

  handleConfigDataChanged(data) {
    if (_.isNil(data)) {
      this.setState({ editValidated: false, validatedPass: false });
      return;
    }
    const { labels, configsByLabel } = groupLabels(data, data);
    this.setState({
      configData: data,
      labels,
      configsByLabel,
      editValidated: true,
      validatedPass: false,
    });
  }

  handleSyncErrMsg({ result }) {
    if (result) {
      this.setState({
        msg: ["Valid JSON format", MESSAGE.SUCCESS],
      });
    } else {
      this.setState({
        msg: ["Invalid JSON format", MESSAGE.ERROR],
      });
    }
  }

  async handleValidateData() {
    const { configData, editValidated } = this.state;

    if (!editValidated) {
      this.handleSyncErrMsg({ result: false });
      return;
    }

    this.setState({ isValidating: true });

    try {
      const json = _.cloneDeep(configData);
      const newJson = _.map(json, (rule) => {
        return cleanupRule(rule);
      });
      const errMsgList = [];
      const warnIdList = [];
      newJson.forEach((rule, idx) => {
        const errMsg = validateRule(rule, false, newJson);
        const isPassWarn = validateRuleCustom(rule);

        if (errMsg !== "")
          errMsgList.push(
            `${rule.id ? `Rule ID ${rule.id}` : `Index [${idx}]`}: ${errMsg}`
          );
        if (!isPassWarn && rule.enabled === true) warnIdList.push(rule.id);
      });

      const newWarnMsg = `The Customs section is empty [RuleID]: ${warnIdList.join(
        ", "
      )}`;

      // Frontend validation errors
      if (errMsgList.length > 0) {
        const unionMsg = _.union(errMsgList);
        throw new Error(unionMsg.join("; "));
      }

      // API validation errors
      const { isValid, reason } = await NetworkAPI.validateCustomUprSetting({
        setting: newJson,
      });
      if (!isValid) {
        throw new Error(reason || "Validation failed!");
      }

      // Warnings
      let msg = [];
      if (warnIdList.length > 0) {
        msg = [newWarnMsg, MESSAGE.WARNING];
      } else {
        msg = ["Validation passed!", MESSAGE.SUCCESS];
      }
      this.setState({ msg, validatedPass: true });
    } catch (error) {
      this.setState({
        msg: [error.toString(), MESSAGE.ERROR],
        validatedPass: false,
      });
    } finally {
      this.setState({ isValidating: false });
    }
  }

  handleDataChanged() {
    const { configData } = this.state;

    const { handleDataChanged } = this.props;
    if (handleDataChanged) {
      const newJson = _.map(configData, (rule) => {
        return cleanupRule(rule);
      });
      handleDataChanged(newJson);
    }
  }

  render() {
    const {
      handleClose,

      networkId,
      networkInfo,
      units,
      virtualPlacements,
      placements,
    } = this.props;

    const {
      configData,
      configsByLabel,
      labels,
      msg,
      isValidating,
      validatedPass,
    } = this.state;

    return (
      <ModalWrapper
        isOpen={true}
        showCloseFooter={false}
        width="65%"
        title="Edit Custom UPR JSON"
        customOverlayStyle={{ zIndex: 100 }}
      >
        <div className="mb-6 flex gap-4" style={{ height: "520px" }}>
          <div className="w-1/3">
            <EditableJsonEditor
              originalConfig={configData}
              onChange={({ parsed }) => {
                this.handleConfigDataChanged(parsed);
              }}
              handleCheckFormat={this.handleSyncErrMsg}
            />
          </div>
          <div className="flex w-2/3 flex-col">
            <div className="text-gray-600">Preview</div>
            <div
              className="rounded flex-1 overflow-y-auto bg-gray-200 p-4"
              style={{ paddingBottom: "280px" }}
            >
              {labels.map((label) => {
                return (
                  <div
                    id={label.value}
                    key={label.value}
                    className="border mb-6 border-gray-400"
                  >
                    <div className="flex items-center justify-between  bg-white p-4 text-xl font-semibold text-gray-800">
                      <div>
                        {label.value === "undefined" ? "No label" : label.value}
                      </div>

                      <div className="flex items-center gap-2">
                        <>
                          <div
                            className="text-right"
                            style={{ width: "6.5rem" }}
                          >
                            {label.numOfItems}
                          </div>
                        </>
                      </div>
                    </div>

                    <UPRSettingsTable
                      networkId={networkId}
                      allItems={configsByLabel[label.value]}
                      isSearch={false}
                      items={configsByLabel[label.value]}
                      networkInfo={networkInfo}
                      units={units}
                      virtualPlacements={virtualPlacements}
                      placements={placements}
                      readOnly={true}
                      place="left center"
                    ></UPRSettingsTable>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <ModalFooter
          msg={msg}
          redo={true}
          switchToCloseOnSuccess={false}
          isLoading={isValidating}
          onCancel={handleClose}
          onConfirm={this.handleDataChanged}
          confirmText="Replace"
          disableConfirm={!validatedPass}
        >
          <button
            type="button"
            className={buttonModalConfirmClass}
            onClick={this.handleValidateData}
          >
            Validate
          </button>
        </ModalFooter>
      </ModalWrapper>
    );
  }
}

export default EditCustomUPRDataModal;
